import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Key,
  Plus,
  Copy,
  Trash2,
  RefreshCw,
  Eye,
  EyeOff,
  Shield,
  Clock,
  Activity,
  AlertTriangle,
  CheckCircle,
  ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AVAILABLE_PERMISSIONS = [
  { key: 'messages:send', label: 'Envoyer des messages', description: 'POST /messages' },
  { key: 'messages:read', label: 'Lire les messages', description: 'GET /messages' },
  { key: 'contacts:read', label: 'Lire les contacts', description: 'GET /contacts' },
  { key: 'contacts:write', label: 'Modifier les contacts', description: 'POST/PUT /contacts' },
  { key: 'templates:read', label: 'Accéder aux templates', description: 'GET /templates' },
  { key: 'analytics:read', label: 'Accéder aux analytics', description: 'GET /analytics' },
  { key: 'webhooks:manage', label: 'Gérer les webhooks', description: 'CRUD /webhooks' },
];

const APIKeys = ({ embedded = false }) => {
  const { t } = useTranslation();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [showFullKey, setShowFullKey] = useState(false);
  
  // Form state
  const [keyName, setKeyName] = useState('');
  const [keyDescription, setKeyDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState(['messages:send', 'messages:read']);
  const [rateLimit, setRateLimit] = useState(1000);

  const fetchApiKeys = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/v2/gateway/keys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiKeys(response.data);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      toast.error('Erreur lors du chargement des clés API');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const handleCreateKey = async () => {
    if (!keyName.trim()) {
      toast.error('Veuillez entrer un nom pour la clé');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/v2/gateway/keys`, {
        name: keyName,
        scopes: selectedPermissions,
        allowed_endpoints: ['*'],
        allowed_ips: []
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewKey(response.data);
      setShowCreateDialog(false);
      setShowKeyDialog(true);
      
      // Reset form
      setKeyName('');
      setKeyDescription('');
      setSelectedPermissions(['messages:send', 'messages:read']);
      setRateLimit(1000);
      
      // Refresh list
      fetchApiKeys();
      
      toast.success('Clé API créée avec succès !');
    } catch (error) {
      console.error('Error creating API key:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la clé');
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir révoquer cette clé ? Cette action est irréversible.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/v2/gateway/keys/${keyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Clé API révoquée');
      fetchApiKeys();
    } catch (error) {
      toast.error('Erreur lors de la révocation');
    }
  };

  const handleCopyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('Clé copiée dans le presse-papiers');
  };

  const togglePermission = (permission) => {
    setSelectedPermissions(prev => 
      prev.includes(permission)
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Jamais';
    return new Date(dateString).toLocaleString('fr-FR');
  };

  const content = (
    <div className="space-y-6" data-testid="api-keys-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Clés API</h1>
          <p className="text-slate-600 mt-1">
            Gérez vos clés d'accès à l'API publique B2RCS
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-api-key-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nouvelle clé API
        </Button>
      </div>

      {/* Documentation Link */}
        <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Key className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Documentation API</p>
                  <p className="text-sm text-slate-600">
                    Consultez notre documentation complète pour intégrer l'API B2RCS
                  </p>
                </div>
              </div>
              <Button variant="outline" className="gap-2">
                <ExternalLink className="h-4 w-4" />
                Voir la documentation
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* API Keys Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Mes clés API
            </CardTitle>
            <CardDescription>
              {apiKeys.length} clé{apiKeys.length > 1 ? 's' : ''} configurée{apiKeys.length > 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : apiKeys.length === 0 ? (
              <div className="text-center py-12">
                <Key className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucune clé API créée</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setShowCreateDialog(true)}
                >
                  Créer ma première clé
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Préfixe</TableHead>
                    <TableHead>Permissions</TableHead>
                    <TableHead>Limite</TableHead>
                    <TableHead>Dernière utilisation</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apiKeys.map((key) => (
                    <TableRow key={key.key_id}>
                      <TableCell className="font-medium">{key.name}</TableCell>
                      <TableCell>
                        <code className="bg-slate-100 px-2 py-1 rounded text-sm">
                          {key.prefix}...
                        </code>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {key.permissions.slice(0, 2).map(p => (
                            <Badge key={p} variant="secondary" className="text-xs">
                              {p.split(':')[0]}
                            </Badge>
                          ))}
                          {key.permissions.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{key.permissions.length - 2}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">
                          {key.rate_limit}/h
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Clock className="h-3 w-3" />
                          {key.last_used_at ? formatDate(key.last_used_at) : 'Jamais'}
                        </div>
                      </TableCell>
                      <TableCell>
                        {key.is_active ? (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="destructive">
                            Révoquée
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRevokeKey(key.key_id)}
                            disabled={!key.is_active}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create Key Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Créer une clé API
              </DialogTitle>
              <DialogDescription>
                Créez une nouvelle clé pour accéder à l'API B2RCS
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="key-name">Nom de la clé *</Label>
                <Input
                  id="key-name"
                  placeholder="ex: Production, Développement, CRM..."
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="key-desc">Description (optionnel)</Label>
                <Input
                  id="key-desc"
                  placeholder="Usage de cette clé..."
                  value={keyDescription}
                  onChange={(e) => setKeyDescription(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Permissions</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                  {AVAILABLE_PERMISSIONS.map((perm) => (
                    <div
                      key={perm.key}
                      className="flex items-center justify-between py-2 px-2 hover:bg-slate-50 rounded"
                    >
                      <div>
                        <p className="text-sm font-medium">{perm.label}</p>
                        <p className="text-xs text-slate-500">{perm.description}</p>
                      </div>
                      <Switch
                        checked={selectedPermissions.includes(perm.key)}
                        onCheckedChange={() => togglePermission(perm.key)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="rate-limit">Limite de requêtes (par heure)</Label>
                <Input
                  id="rate-limit"
                  type="number"
                  min={100}
                  max={100000}
                  value={rateLimit}
                  onChange={(e) => setRateLimit(parseInt(e.target.value))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Annuler
              </Button>
              <Button onClick={handleCreateKey}>
                Créer la clé
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Show New Key Dialog */}
        <Dialog open={showKeyDialog} onOpenChange={setShowKeyDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                Clé API créée avec succès !
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Important !</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Cette clé ne sera affichée qu'une seule fois. Copiez-la et conservez-la en lieu sûr.
                    </p>
                  </div>
                </div>
              </div>

              {newKey && (
                <div className="space-y-2">
                  <Label>Votre clé API</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                      <Input
                        type={showFullKey ? 'text' : 'password'}
                        value={newKey.api_key}
                        readOnly
                        className="pr-20 font-mono text-sm"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1/2 -translate-y-1/2"
                        onClick={() => setShowFullKey(!showFullKey)}
                      >
                        {showFullKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleCopyKey(newKey.api_key)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              <div className="text-sm text-slate-600 space-y-1">
                <p><strong>Nom:</strong> {newKey?.name}</p>
                <p><strong>Permissions:</strong> {newKey?.permissions.join(', ')}</p>
                <p><strong>Limite:</strong> {newKey?.rate_limit} requêtes/heure</p>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={() => setShowKeyDialog(false)}>
                J'ai copié ma clé
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
  );

  if (embedded) {
    return content;
  }

  return <Layout>{content}</Layout>;
};

export default APIKeys;
